import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { v4 as uuidv4 } from 'uuid';

import { HeroReport } from '../../../model/hero-report/hero-report.model';
import { HeroReportService } from '../../../model/hero-report/hero-report.service';
import { HeroReportImageService } from '../../../model/hero-report-image/hero-report-image.service';
import { StepperHeaderComponent } from '../../../shared/stepper-header/stepper-header.component';
import { ItemFormComponent } from '../../hero-report/item-form/item-form.component';
import { SreDetailsFormComponent, buildSreDetailsForm } from '../sre-details-form/sre-details-form.component';
import { SrePhotoGridComponent } from '../sre-photo-grid/sre-photo-grid.component';
import getDataUrlImageType from '../../../utilities/getDataUrlImageType';
import { mgrsCenter } from '../../../utilities/mgrs';
import { SRE_IMAGE_TYPE, SrePhoto, sreDetailsFromForm } from '../../../utilities/sreReport';

/**
 * Creating an SRE report: step 1 is the possible identification and the photos (1 to 10, each with an
 * optional caption); step 2 is the details form. Submitting saves the report as COMPLETED with report
 * type SRE, then uploads the photos as OVERALL images in the order shown.
 */
@Component({
  selector: 'app-sre-report-stepper',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    ButtonModule,
    StepperHeaderComponent,
    ItemFormComponent,
    SrePhotoGridComponent,
    SreDetailsFormComponent,
  ],
  templateUrl: './sre-report-stepper.component.html',
  styleUrls: ['../../hero-report-stepper/hero-report-stepper.component.scss'],
})
export class SreReportStepperComponent implements OnInit {
  step = 0;
  isSubmitting = false;
  submitFailed = false;
  photosTouched = false;

  itemForm!: FormGroup;
  detailsForm!: FormGroup;
  photos: SrePhoto[] = [];

  steps = [
    { label: $localize`:@@stepper.item:Item` },
    { label: $localize`:@@sre.stepper.details:Details` },
  ];

  // Kept so a retry after a failed photo upload carries on instead of creating the report twice.
  private createdReportId: string | null = null;
  private uploadedPhotoIndexes = new Set<number>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private heroReportService: HeroReportService,
    private heroReportImageService: HeroReportImageService,
  ) {}

  ngOnInit() {
    const lat = this.route.snapshot.queryParamMap.get('lat');
    const lng = this.route.snapshot.queryParamMap.get('lng');
    if (!lat || !lng) {
      this.router.navigate(['/events']);
      return;
    }

    this.itemForm = this.fb.group({
      possibleIdentification: ['', Validators.required],
      evacuationPerimeterM: [null],
      latitude: [parseFloat(lat), Validators.required],
      longitude: [parseFloat(lng), Validators.required],
    });
    this.detailsForm = buildSreDetailsForm(this.fb);
  }

  onClassificationSelect(event: { value: string[]; evacuationRadius?: number }) {
    this.itemForm.patchValue({
      possibleIdentification: event.value,
      evacuationPerimeterM: event.evacuationRadius ?? 0,
    });
  }

  onPhotosChange(photos: SrePhoto[]) {
    this.photos = photos;
  }

  get photoError(): boolean {
    return this.photosTouched && this.photos.length === 0;
  }

  nextStep() {
    this.itemForm.markAllAsTouched();
    this.photosTouched = true;

    if (this.itemForm.valid && this.photos.length > 0) {
      this.step = 1;
    }
  }

  prevStep() {
    this.step = 0;
  }

  async submit() {
    this.detailsForm.markAllAsTouched();
    if (this.detailsForm.invalid || this.itemForm.invalid || this.photos.length === 0) {
      return;
    }

    this.isSubmitting = true;
    this.submitFailed = false;

    try {
      if (!this.createdReportId) {
        const itemValues = this.itemForm.value;
        const details = sreDetailsFromForm(this.detailsForm.value);
        // The report is plotted at the center of the MGRS square it names. The place picked on the map to
        // start the report is only used if there is no valid MGRS, which the form does not allow.
        const center = mgrsCenter(details.mgrs);
        const report = {
          id: uuidv4(),
          reportType: 'SRE',
          status: 'COMPLETED',
          latitude: center?.latitude ?? itemValues.latitude,
          longitude: center?.longitude ?? itemValues.longitude,
          possibleIdentification: itemValues.possibleIdentification,
          evacuationPerimeterM: itemValues.evacuationPerimeterM ?? 0,
          casualties: '',
          additionalHazards: '',
          ...details,
        } as unknown as HeroReport;

        this.createdReportId = (await this.heroReportService.create(report)).id;
      }

      for (let index = 0; index < this.photos.length; index++) {
        if (this.uploadedPhotoIndexes.has(index)) {
          continue;
        }
        const photo = this.photos[index];
        await this.heroReportImageService.createAndUpload({
          id: uuidv4(),
          url: photo.url,
          type: SRE_IMAGE_TYPE,
          fileType: getDataUrlImageType(photo.url),
          heroReportId: this.createdReportId,
          displayOrder: index,
          caption: photo.caption,
        });
        this.uploadedPhotoIndexes.add(index);
      }

      this.router.navigate(['/events']);
    } catch (error) {
      console.error('Unable to save the SRE report', error);
      this.submitFailed = true;
    } finally {
      this.isSubmitting = false;
    }
  }

  goBack() {
    this.router.navigate(['/events'], { queryParams: { recenter: 'true' } });
  }
}
